// ./src/components/features/MarketingChannels/EventMarketingChannelsBoard/EventMarketingChannelsBoard.tsx
//
// Per-event view over the account's marketing channels: for each channel it
// builds the event-specific tracking URL (<eventUrlBase>?ch=<code>) and offers
// Copy + QR. It does NOT edit or remove channels — those are account-level and
// live on the Library page (the page shows a banner saying so). Responsive:
// table on desktop (>= md), cards below. Read-only + tap-safe.
// ⚠ 2026-10-07 (owner: swipeable actions on phones; skeletons per breakpoint) — on a phone Copy link + QR are SWIPE
// actions (swipe right: they are "do something" actions), the card carries no buttons; the first read draws table
// rows on desktop and cards below, each the shape of what replaces it.

import React, { useCallback, useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Code,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Button,
  Skeleton,
  Table,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { MarketingChannel } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { useToast } from "@/hooks";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, CardSkeletonActions, CardSkeletonLines, countCardActions } from "@/components/generic";

export interface EventMarketingChannelsBoardProps {
  channels: MarketingChannel[];
  /** The event's public base URL, e.g. `https://host/e/<slug>` (no query string). */
  eventUrlBase: string;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  onAddChannel?: () => void;
  onShowQr?: (channel: MarketingChannel, url: string) => void;
  /**
   * A search term is narrowing the list. An empty result then means "no match" —
   * NOT "nothing here yet", so show that instead of the first-run Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

// Append (or merge) the ?ch=<code> marker onto the event base URL.
// ⚠ Exported for the create wizard's finish screen — ONE builder of a tracked link, so the link a host shares from
// there is byte-for-byte the one this board shows and the report counts.
export const buildChannelUrl = (base: string, code?: string | null): string => {
  if (!code) {
    return base;
  }

  const sep = base.includes("?") ? "&" : "?";

  return `${base}${sep}ch=${encodeURIComponent(code)}`;
};

const ChannelIcon: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <ThemeIcon variant="light" color="teal" radius="xl" size={size}>
    <AppIcon iconName="Speakerphone" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const EventMarketingChannelsBoardComponent: React.FC<EventMarketingChannelsBoardProps> = ({
  channels,
  eventUrlBase,
  isLoaded,
  onAddChannel,
  onShowQr,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["marketingChannels", "common"]);
  const { isDesktop } = useAppEnvironment();

  // Per-row "Copied" feedback (which channel's link was last copied).
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const toast = useToast();

  // `announce`: the phone's swipe action slides out of view as it runs, so its "Copied" icon is never seen — a phone
  // gets a toast instead. A copy that FAILS always says so (it used to be swallowed: nothing happened, nothing said why).
  const copyUrl = useCallback(
    (channel: MarketingChannel, url: string, announce = false) => {
      const failed = () => toast.error(t("marketingChannels:event.copyFailed", { defaultValue: "Couldn’t copy the link" }));

      if (typeof navigator === "undefined" || !navigator.clipboard) {
        failed();

        return;
      }

      navigator.clipboard
        .writeText(url)
        .then(() => {
          setCopiedId(channel.id ?? null);
          window.setTimeout(() => setCopiedId((cur) => (cur === channel.id ? null : cur)), 1500);

          if (announce) {
            toast.success(t("marketingChannels:event.copied", { defaultValue: "Link copied" }));
          }
        })
        .catch(failed);
    },
    [toast, t],
  );

  const rows = useMemo(
    () =>
      channels.map((channel) => ({
        channel,
        url: buildChannelUrl(eventUrlBase, channel.code),
      })),
    [channels, eventUrlBase]
  );

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  // Desktop: real table rows in the real table shell (same columns and widths as the loaded rows).
  if (showSkeleton && isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }} aria-busy="true">
        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 260 }}>{t("marketingChannels:event.columns.name", { defaultValue: "Channel" })}</Table.Th>
              <Table.Th>{t("marketingChannels:event.columns.url", { defaultValue: "Tracking link" })}</Table.Th>
              <Table.Th style={{ width: 200 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <Table.Tr key={i}>
                <Table.Td style={{ width: 260 }}>
                  <Group gap="sm" wrap="nowrap" align="center">
                    <Skeleton circle height={34} width={34} />
                    <Box style={{ flex: 1 }}>
                      <Skeleton height={16} width="70%" mb={6} />
                      <Skeleton height={18} width={70} />
                    </Box>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Skeleton height={14} width="80%" />
                </Table.Td>
                <Table.Td style={{ width: 200 }}>
                  <Group gap="xs" wrap="nowrap">
                    <Skeleton height={36} width={110} radius="sm" />
                    <Skeleton height={36} width={64} radius="sm" />
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  // Phones + tablets: the card's shape — icon, name + code, the link; the two buttons on tablets only. The link is a whole
  // URL broken anywhere, so how many lines it takes depends on the width: it is drawn as THIS event's own link behind a
  // Skeleton, so it wraps exactly where the real one does (ROADMAP #127).
  if (showSkeleton) {
    return (
      <Stack gap="sm" aria-busy="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <Paper key={i} p="md" withBorder radius="md">
            <Stack gap="sm">
              <Group gap="md" wrap="nowrap">
                <Skeleton circle height={40} width={40} />
                <CardSkeletonLines count={2} gap={0} />
                <CardSkeletonActions count={countCardActions(true, onShowQr)} />
              </Group>
              <Skeleton>
                <Text size="md" style={{ wordBreak: "break-all" }}>
                  {buildChannelUrl(eventUrlBase, "code")}
                </Text>
              </Skeleton>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (!showSkeleton && channels.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("marketingChannels:event.noMatch", { defaultValue: "No channels match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("marketingChannels:event.emptyTitle", { defaultValue: "No marketing channels yet" })}
          description={t("marketingChannels:event.emptyDescription", {
            defaultValue:
              "Add a channel (e.g. Facebook → fb) to get a trackable link and QR code for this event.",
          })}
          iconName="Speakerphone"
          actionLabel={t("marketingChannels:event.addAction", { defaultValue: "Add channel" })}
          onAction={onAddChannel}
          size="md"
        />
      </Paper>
    );
  }

  const copyLabel = (channel: MarketingChannel): string =>
    copiedId === channel.id
      ? t("common:copied", { defaultValue: "Copied" })
      : t("marketingChannels:event.copy", { defaultValue: "Copy link" });

  const RowActions: React.FC<{ channel: MarketingChannel; url: string; compact?: boolean }> = ({
    channel,
    url,
    compact,
  }) => {
    if (compact) {
      return (
        <Group gap={4} wrap="nowrap">
          <Tooltip label={copyLabel(channel)} withArrow position="bottom">
            <ActionIcon variant="subtle" color="teal" size="md" onClick={() => copyUrl(channel, url)} aria-label={copyLabel(channel)}>
              <AppIcon iconName={copiedId === channel.id ? "Check" : "Copy"} size={16} />
            </ActionIcon>
          </Tooltip>
          {onShowQr && (
            <Tooltip label={t("marketingChannels:event.qr", { defaultValue: "QR code" })} withArrow position="bottom">
              <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onShowQr(channel, url)} aria-label={t("marketingChannels:event.qr", { defaultValue: "QR code" })}>
                <AppIcon iconName="Qrcode" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      );
    }

    return (
      <Group gap="xs" wrap="nowrap">
        <Button
          variant="light"
          color="teal"
          size="md"
          leftSection={<AppIcon iconName={copiedId === channel.id ? "Check" : "Copy"} size={14} />}
          onClick={() => copyUrl(channel, url)}
        >
          {copyLabel(channel)}
        </Button>
        {onShowQr && (
          <Button
            variant="light"
            color="blue"
            size="md"
            leftSection={<AppIcon iconName="Qrcode" size={14} />}
            onClick={() => onShowQr(channel, url)}
          >
            {t("marketingChannels:event.qr", { defaultValue: "QR" })}
          </Button>
        )}
      </Group>
    );
  };

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 260 }}>{t("marketingChannels:event.columns.name", { defaultValue: "Channel" })}</Table.Th>
              <Table.Th>{t("marketingChannels:event.columns.url", { defaultValue: "Tracking link" })}</Table.Th>
              <Table.Th style={{ width: 200 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(({ channel, url }) => (
              <Table.Tr key={channel.id}>
                <Table.Td style={{ width: 260 }}>
                  <Group gap="sm" wrap="nowrap" align="center">
                    <ChannelIcon />
                    <Box style={{ minWidth: 0 }}>
                      <Text size="md" fw={600} lineClamp={1}>
                        {channel.name || "—"}
                      </Text>
                      <Code>{`?ch=${channel.code}`}</Code>
                    </Box>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
                    {url}
                  </Text>
                </Table.Td>
                <Table.Td style={{ width: 200 }}>
                  <RowActions channel={channel} url={url} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  // ── Tablet / mobile cards ──
  const cardBody = (channel: MarketingChannel, url: string, showActions: boolean) => (
    <Paper p="md" withBorder radius="md">
      <Stack gap="sm">
        <Group gap="md" wrap="nowrap" align="center">
          <ChannelIcon size={40} />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={600} lineClamp={1}>
              {channel.name || "—"}
            </Text>
            <Code>{`?ch=${channel.code}`}</Code>
          </Box>
          {showActions && <RowActions channel={channel} url={url} compact />}
        </Group>
        <Text size="md" c="dimmed" style={{ wordBreak: "break-all" }}>
          {url}
        </Text>
      </Stack>
    </Paper>
  );

  // The same two actions as the buttons; both are "do something" actions, so they sit on the LEFT (swipe right).
  const swipeActionsFor = (channel: MarketingChannel, url: string): SwipeAction[] => {
    const actions: SwipeAction[] = [
      {
        key: "copy",
        icon: copiedId === channel.id ? "Check" : "Copy",
        label: copyLabel(channel),
        color: "teal",
        secondary: true,
        onClick: () => {
          closeAllCards();
          copyUrl(channel, url, true);
        },
      },
    ];

    if (onShowQr) {
      actions.push({
        key: "qr",
        icon: "Qrcode",
        label: t("marketingChannels:event.qr", { defaultValue: "QR code" }),
        color: "blue",
        secondary: true,
        onClick: () => {
          closeAllCards();
          onShowQr(channel, url);
        },
      });
    }

    return actions;
  };

  return (
    <Stack gap="sm">
      {rows.map(({ channel, url }) => {
        const id = channel.id || "";

        return (
          <Box key={id}>
            {/* Phones: swipe right for Copy link / QR — no buttons on the card. */}
            <Box hiddenFrom="sm">
              <SwipeableCard
                leftSwipeActions={swipeActionsFor(channel, url)}
                forceClose={shouldForceClose(id)}
                onForceCloseComplete={() => handleForceCloseComplete(id)}
                onSwipeStateChange={(isOpen) => {
                  if (isOpen) {
                    openCard(id);
                  }
                }}
                borderRadius="var(--mantine-radius-md)"
              >
                {cardBody(channel, url, false)}
              </SwipeableCard>
            </Box>
            {/* Tablets: the visible buttons. */}
            <Box visibleFrom="sm">{cardBody(channel, url, true)}</Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export const EventMarketingChannelsBoard = React.memo(EventMarketingChannelsBoardComponent);
